/**
 * YUMIX — Tiny synthesized celebration cue.
 *
 * Why no audio file? Pure synthesis means:
 *   • Zero network call
 *   • Zero asset to ship
 *   • Zero codec / format compatibility issues across iOS/Android
 *   • Bundle stays light
 *
 * The cue is a soft major-triad arpeggio (C5 → E5 → G5) with quick attack
 * and exponential decay — friendly and non-intrusive. Total length ~450 ms.
 *
 * Respects user preference via localStorage `yumix_sound_muted` flag.
 * Modern browsers require a user gesture before AudioContext can fire,
 * so the FIRST call may silently no-op on a fresh page load — that's fine,
 * the meal analysis itself comes from a user-initiated upload, so by the
 * time we celebrate, the page already has a gesture in its history.
 */

const STORAGE_KEY = 'yumix_sound_muted';

let _ctx = null;
function getCtx() {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _ctx = new Ctx();
    return _ctx;
  } catch {
    return null;
  }
}

export function isSoundMuted() {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}

export function setSoundMuted(muted) {
  try { localStorage.setItem(STORAGE_KEY, muted ? '1' : '0'); } catch { /* noop */ }
}

/**
 * Plays a soft celebratory C-major arpeggio.
 * Silent if muted, no AudioContext, or any failure.
 */
export function playCelebrationCue() {
  if (isSoundMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  try {
    // Resume on user gesture if needed (iOS Safari quirk)
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    // C5 = 523.25, E5 = 659.25, G5 = 783.99 — bright major triad
    const notes = [523.25, 659.25, 783.99];
    const startGap = 0.07; // ~70 ms between notes — fast arpeggio
    const noteDur = 0.28;
    const peakGain = 0.10; // soft, ~-20 dB to avoid startle

    const now = ctx.currentTime;
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine'; // pure tone — gentle
      osc.frequency.setValueAtTime(freq, now + i * startGap);

      const t0 = now + i * startGap;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(peakGain, t0 + 0.015); // 15 ms attack
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + noteDur); // decay

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + noteDur + 0.05);
    });
  } catch {
    /* swallow — sound is non-critical */
  }
}
